import React, { useMemo, useCallback, useState } from 'react';
import dayjs from 'dayjs';
import { Card, Button } from 'react-bootstrap';
import { FaEdit } from 'react-icons/fa';
import { useNavigate } from 'react-router';
import Kanban from '../DesignSystems/Kanban';
import type { KanbanBoard, KanbanCard } from '../DesignSystems/Kanban';
import {
  useIncidentActions,
  useUpdateIncidentAction,
} from './hooks/incidentActionHooks';
import { ACTION_STATUS_OPTIONS } from './ICSConstants';
import Loading from '../Loading/Loading';

interface ActionsKanbanProps {
  incidentId: string;
  onEditAction: (action: any) => void;
}

const ActionsKanban: React.FC<ActionsKanbanProps> = ({
  incidentId,
  onEditAction,
}) => {
  const navigate = useNavigate();
  const { data: actions, isLoading } = useIncidentActions();
  const updateActionMutation = useUpdateIncidentAction();
  const [showArchived, setShowArchived] = useState(false);

  const isOverdue = (status: string, targetDate: Date | null) => {
    if (!targetDate) return false;
    const now = new Date();
    const target = new Date(targetDate);
    return (
      (status === 'Current/Ongoing' || status === 'Closed') && target < now
    );
  };

  const formatDate = (date: Date | null) => {
    if (!date) return 'Not set';
    return dayjs(date).format('MM/DD/YYYY HH:mm');
  };

  const filteredActions = useMemo(() => {
    if (!actions) return [];
    return actions.filter((action: any) =>
      showArchived ? action.archived : !action.archived
    );
  }, [actions, showArchived]);

  const board: KanbanBoard = useMemo(() => {
    const columns = ACTION_STATUS_OPTIONS.map((status) => ({
      id: status.value,
      title: status.label,
      cards: [] as KanbanCard[],
    }));

    if (filteredActions) {
      filteredActions.forEach((action) => {
        const column = columns.find((col) => col.id === action.status);
        if (column) {
          column.cards.push({
            id: action.id,
            title: action.name,
            description: action.description,
            metadata: action,
          });
        }
      });
    }

    return { columns };
  }, [filteredActions]);

  const handleCardMove = useCallback(
    async (card: KanbanCard, source: any, destination: any) => {
      if (card.metadata && source.id !== destination.id) {
        const action = card.metadata;

        try {
          await updateActionMutation.mutateAsync({
            ...action,
            id: action.id,
            status: destination.id,
          } as any);
        } catch (error) {
          // Error handling is now done in the hook
          console.error('Error updating action status:', error);
        }
      }
    },
    [updateActionMutation]
  );

  const renderCard = (card: KanbanCard) => {
    const action = card.metadata;
    if (!action) return null;

    const overdue = isOverdue(action.status, action.target_date);

    return (
      <Card
        className={`mb-2 border ${overdue ? 'border-danger' : 'border-secondary'} bg-white`}
        onClick={() =>
          navigate(
            `/iap/${incidentId}/actions/${action.action_id || action.id}`
          )
        }
        style={{
          cursor: 'pointer',
          borderLeft: overdue ? '4px solid #dc3545' : '4px solid #6c757d',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
        }}
      >
        <Card.Body className="p-2">
          <div className="d-flex justify-content-between align-items-start mb-1">
            <h6 className="mb-0 text-dark flex-grow-1">{card.title}</h6>
            <div className="d-flex gap-1 align-items-center">
              {overdue && (
                <span
                  className="badge bg-danger text-uppercase me-1"
                  style={{ fontSize: '0.65rem' }}
                >
                  OVERDUE
                </span>
              )}
              <Button
                size="sm"
                variant="outline-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditAction(action);
                }}
                style={{ padding: '0.25rem 0.5rem' }}
                title="Edit action"
              >
                <FaEdit size={12} />
              </Button>
            </div>
          </div>
          {card.description && (
            <p className="text-muted small mb-1" style={{ fontSize: '0.8rem' }}>
              {card.description}
            </p>
          )}
          <div className="small">
            <div className="d-flex justify-content-between mb-1">
              <span className="text-secondary">POC:</span>
              <span className="text-dark">
                {(action as any).poc_user_name ||
                  (action as any).user_name ||
                  'Unassigned'}
              </span>
            </div>
            <div className="d-flex justify-content-between mb-1">
              <span className="text-secondary">Start:</span>
              <span className="text-dark">{formatDate(action.start_date)}</span>
            </div>
            <div className="d-flex justify-content-between mb-1">
              <span className="text-secondary">Target:</span>
              <span className="text-dark">
                {formatDate(action.target_date)}
              </span>
            </div>
            <div className="d-flex justify-content-between mb-1">
              <span className="text-secondary">Actual:</span>
              <span className="text-dark">
                {formatDate(action.actual_date)}
              </span>
            </div>
            {action.checklist && action.checklist.length > 0 && (
              <div className="mt-2">
                <div className="text-secondary mb-1">Checklist:</div>
                <div style={{ maxHeight: '60px', overflowY: 'auto' }}>
                  {action.checklist.map((item: any, index: number) => (
                    <div
                      key={index}
                      className="d-flex align-items-center"
                      style={{ fontSize: '0.75rem' }}
                    >
                      <input
                        type="checkbox"
                        checked={item.checked}
                        readOnly
                        className="me-1"
                        style={{ width: '12px', height: '12px' }}
                      />
                      <span
                        className={
                          item.checked
                            ? 'text-decoration-line-through text-muted'
                            : ''
                        }
                      >
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>
    );
  };

  if (isLoading) {
    return <Loading />;
  }

  const getColumnStyle = (columnId: string) => {
    // Add colored border-top based on status
    if (columnId === 'Planned') {
      return { borderTop: '4px solid #0d6efd' };
    } else if (columnId === 'Current/Ongoing') {
      return { borderTop: '4px solid #ffc107' };
    } else if (columnId === 'Closed') {
      return { borderTop: '4px solid #28a745' };
    }
    return {};
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            id="showArchivedToggle"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="showArchivedToggle">
            Show Archived
          </label>
        </div>
      </div>
      <Kanban
        board={board}
        onCardMove={handleCardMove}
        onCardClick={(card) => onEditAction(card.metadata)}
        renderCard={renderCard}
        disableCardDrag={false}
        columnStyle={getColumnStyle}
      />
    </div>
  );
};

export default ActionsKanban;
